import { defineComponent, ref, toRef, provide, useTemplateRef, onBeforeMount, watch, computed, createElementBlock, openBlock, createBlock, createCommentVNode, createVNode, unref, resolveDynamicComponent, withCtx, createTextVNode, toDisplayString, renderSlot, normalizeProps, guardReactiveProps, isRef } from "vue";
import { useSeoMeta } from "@unhead/vue";
import { useFavicon } from "@vueuse/core";
import { isClient } from "@scalar/api-client/v2/blocks/operation-code-sample";
import { useWorkspaceStoreEvents, ApiReferenceLayout } from "@scalar/api-reference";
import { safeLocalStorage, REFERENCE_LS_KEYS } from "@scalar/helpers/object/local-storage";
import { makeUrlAbsolute } from "@scalar/helpers/url/make-url-absolute";
import { redirectToProxy } from "@scalar/oas-utils/helpers";
import { dereference } from "@scalar/openapi-parser";
import { useColorMode } from "@scalar/use-hooks/useColorMode";
import { createWorkspaceStore } from "@scalar/workspace-store/client";
import { emitCustomEvent } from "@scalar/workspace-store/events";
import { parseDocument } from "@scalar-org/parser";
import _sfc_main$1 from "./DocumentSelector.vue.js";
import { getDocumentName } from "./helpers/get-document-name.js";
import { mapConfiguration } from "./helpers/map-configuration.js";
import { useMultipleDocuments } from "./helpers/use-multiple-documents.js";
const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "ApiReferencePro",
  props: {
    uid: {},
    configuration: {}
  },
  emits: ["changeTheme", "updateContent", "loadSwaggerFile", "linkSwaggerFile", "toggleDarkMode"],
  setup(__props) {
    const props = __props;
    const store = createWorkspaceStore();
    const configurationOverrides = ref({});
    const {
      availableDocuments,
      selectedConfiguration,
      selectedDocumentIndex,
      isIntersectionEnabled,
      hash,
      hashPrefix
    } = useMultipleDocuments({
      configuration: toRef(props, "configuration"),
      configurationOverrides,
      isIntersectionEnabled: ref(false),
      hash: ref(""),
      hashPrefix: ref("")
    });
    const proxy = (input, init) => {
      return fetch(
        redirectToProxy(selectedConfiguration.value.proxyUrl, input.toString()),
        init
      );
    };
    const NAV_STATE_SYMBOL = Symbol();
    provide(NAV_STATE_SYMBOL, {
      isIntersectionEnabled,
      hash,
      hashPrefix,
      basePath: () => selectedConfiguration.value.pathRouting?.basePath,
      generateHeadingSlug: () => selectedConfiguration.value.generateHeadingSlug
    });
    const root = useTemplateRef("root");
    onBeforeMount(() => {
      const storedClient = safeLocalStorage().getItem(
        REFERENCE_LS_KEYS.SELECTED_CLIENT
      );
      if (isClient(storedClient) && !store.workspace["x-scalar-default-client"]) {
        store.update("x-scalar-default-client", storedClient);
      }
    });
    function handleUpdatedDocument(name, config, document) {
      store.update("x-scalar-active-document", name);
      const defaultServer = config.servers?.at(0) ?? document.servers?.at(0);
      if (defaultServer)
        emitCustomEvent(root.value, "scalar-update-selected-server", {
          value: defaultServer.url
        });
    }
    const addOrUpdateDocument = async (config) => {
      const { spec } = await parseDocument(config.content, {
        validate: false
      });
      const dereferenced = await dereference(spec);
      const document = dereferenced.schema ?? spec;
      const name = props.uid ?? getDocumentName({
        name: config.slug || config.title,
        url: config.url,
        document
      });
      if (store.workspace.documents[name] && document) {
        isIntersectionEnabled.value = false;
        await store.replaceDocument(name, document);
        handleUpdatedDocument(name, config, document);
        setTimeout(() => {
          isIntersectionEnabled.value = true;
        }, 300);
      }
      if (document) {
        await store.addDocument({
          name,
          document,
          config: mapConfiguration(config)
        });
        return handleUpdatedDocument(name, config, document);
      }
      if (config.url) {
        await store.addDocument({
          name: config.slug ?? "default",
          url: makeUrlAbsolute(config.url, {
            basePath: selectedConfiguration.value.pathRouting?.basePath
          }),
          fetch: config.fetch ?? proxy,
          config: mapConfiguration(config)
        });
        return handleUpdatedDocument(name, config, document);
      }
    };
    watch(
      [
        () => selectedConfiguration.value.slug,
        () => selectedConfiguration.value.url,
        () => selectedConfiguration.value.content
      ],
      ([newSlug, newUrl, newContent]) => {
        if (newSlug || newUrl || newContent) {
          addOrUpdateDocument(selectedConfiguration.value);
        }
      },
      { immediate: true }
    );
    useWorkspaceStoreEvents(store, root, selectedConfiguration.value);
    watch(
      () => selectedConfiguration.value.defaultHttpClient,
      (newValue) => {
        if (newValue) {
          const { targetKey, clientKey } = newValue;
          const clientId = `${targetKey}/${clientKey}`;
          if (isClient(clientId)) {
            store.update("x-scalar-default-client", clientId);
          }
        }
      },
      { immediate: true }
    );
    function getInitialColorMode(darkMode) {
      if (darkMode === true) {
        return "dark";
      }
      if (darkMode === false) {
        return "light";
      }
      return void 0;
    }
    const { toggleColorMode, isDarkMode } = useColorMode({
      initialColorMode: getInitialColorMode(selectedConfiguration.value.darkMode),
      overrideColorMode: selectedConfiguration.value.forceDarkModeState
    });
    watch(
      () => selectedConfiguration.value.darkMode,
      (isDark) => store.update("x-scalar-dark-mode", !!isDark)
    );
    watch(
      () => isDarkMode.value,
      (newValue) => store.update("x-scalar-dark-mode", newValue),
      { immediate: true }
    );
    watch(
      () => selectedDocumentIndex.value,
      (newValue) => store.update(
        "x-scalar-active-document",
        availableDocuments.value[newValue]?.slug
      ),
      { immediate: true }
    );
    if (selectedConfiguration.value.metaData) {
      useSeoMeta(selectedConfiguration.value.metaData);
    }
    const favicon = computed(() => selectedConfiguration.value.favicon);
    useFavicon(favicon);
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", {
        ref_key: "root",
        ref: root
      }, [
        unref(selectedConfiguration)?.customCss ? (openBlock(), createBlock(resolveDynamicComponent("style"), { key: 0 }, {
          default: withCtx(() => [
            createTextVNode(toDisplayString(unref(selectedConfiguration).customCss), 1)
          ]),
          _: 1
        })) : createCommentVNode("", true),
        createVNode(unref(ApiReferenceLayout), {
          configuration: unref(selectedConfiguration),
          isDark: !!unref(store).workspace["x-scalar-dark-mode"],
          store: unref(store),
          onChangeTheme: _cache[1] || (_cache[1] = ($event) => _ctx.$emit("changeTheme", $event)),
          onLinkSwaggerFile: _cache[2] || (_cache[2] = ($event) => _ctx.$emit("linkSwaggerFile")),
          onLoadSwaggerFile: _cache[3] || (_cache[3] = ($event) => _ctx.$emit("loadSwaggerFile")),
          onToggleDarkMode: _cache[4] || (_cache[4] = () => unref(toggleColorMode)()),
          onUpdateContent: _cache[5] || (_cache[5] = ($event) => _ctx.$emit("updateContent", $event))
        }, {
          "document-selector": withCtx(() => [
            createVNode(_sfc_main$1, {
              modelValue: unref(selectedDocumentIndex),
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => isRef(selectedDocumentIndex) ? selectedDocumentIndex.value = $event : null),
              options: unref(availableDocuments)
            }, null, 8, ["modelValue", "options"])
          ]),
          "content-start": withCtx(() => [
            renderSlot(_ctx.$slots, "content-start")
          ]),
          header: withCtx(({ breadcrumb }) => [
            renderSlot(_ctx.$slots, "header", normalizeProps(guardReactiveProps({ breadcrumb })))
          ]),
          editor: withCtx(() => [
            renderSlot(_ctx.$slots, "editor")
          ]),
          "content-end": withCtx(() => [
            renderSlot(_ctx.$slots, "content-end")
          ]),
          "sidebar-start": withCtx(() => [
            renderSlot(_ctx.$slots, "sidebar-start")
          ]),
          "sidebar-end": withCtx(() => [
            renderSlot(_ctx.$slots, "sidebar-end")
          ]),
          footer: withCtx(() => [
            renderSlot(_ctx.$slots, "footer")
          ]),
          _: 3
        }, 8, ["configuration", "isDark", "store"])
      ], 512);
    };
  }
});
export {
  _sfc_main as default
};
//# sourceMappingURL=ApiReferencePro.vue.js.map
